import { useMemo, useState } from "react";
import { categories, hotList } from "@/mocks/campus";
import SectionHead from "./SectionHead";
import Reveal from "./Reveal";

interface HotRankProps {
  activeCat: string;
  setActiveCat: (slug: string) => void;
}

function fmtCount(n: number) {
  if (n >= 10000) return `${(n / 10000).toFixed(1)}w`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return `${n}`;
}

function FilterChip({ label, slug, active, onClick }: { label: string; slug: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
        active ? "bg-primary-500 text-background-50" : "bg-background-50 text-foreground-600 ring-1 ring-background-300 hover:bg-primary-100 hover:text-primary-800"
      }`}
    >
      {slug !== "all" ? <i className="ri-arrow-drop-right-line text-base leading-none" /> : null}
      {label}
    </button>
  );
}

export default function HotRank({ activeCat, setActiveCat }: HotRankProps) {
  const catMap = useMemo(() => {
    const map = new Map<string, string>();
    categories.forEach((c) => map.set(c.slug, c.name));
    return map;
  }, []);

  const filtered = useMemo(
    () => (activeCat === "all" ? hotList : hotList.filter((h) => h.category === activeCat)),
    [activeCat]
  );
  const shown = filtered.slice(0, 10);

  return (
    <section id="hot" className="bg-background-100/70 scroll-mt-24">
      <div className="mx-auto max-w-7xl px-4 py-12 md:px-6 md:py-16">
        <Reveal>
          <SectionHead
            eyebrow="热门话题"
            title="今日校园吃瓜热榜"
            desc="热度由围观人数与讨论量实时汇总，TOP10 榜单每小时刷新。点击分类，只看你想吃的瓜。"
          />
        </Reveal>

        <Reveal delay={80}>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
            <FilterChip label="全部" slug="all" active={activeCat === "all"} onClick={() => setActiveCat("all")} />
            {categories.map((c) => (
              <FilterChip key={c.slug} label={c.name} slug={c.slug} active={activeCat === c.slug} onClick={() => setActiveCat(c.slug)} />
            ))}
          </div>
        </Reveal>

        {/* 榜单 */}
        <Reveal delay={120}>
          <div className="mt-8 grid gap-4 md:gap-6 lg:grid-cols-2">
            {shown.length === 0 ? (
              <div className="col-span-full flex flex-col items-center gap-3 rounded-3xl bg-background-50 p-12 text-center ring-1 ring-background-200">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400"><i className="ri-inbox-line text-2xl" /></span>
                <p className="text-sm text-foreground-600">该分类今天暂时没有上榜话题，换个分类看看～</p>
                <button type="button" onClick={() => setActiveCat("all")} className="mt-1 rounded-full bg-primary-500 px-4 py-2 text-xs font-semibold text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap">
                  查看全部
                </button>
              </div>
            ) : (
              shown.map((item, idx) => {
                const rank = idx + 1;
                const top3 = rank <= 3;
                return (
                  <article
                    key={`${item.title}-${activeCat}`}
                    className="group flex items-center gap-3 rounded-2xl bg-background-50 p-3.5 ring-1 ring-background-200 transition-colors hover:ring-primary-200 md:gap-4 md:p-4"
                  >
                    <span
                      className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl font-heading text-lg md:h-11 md:w-11 md:text-xl ${
                        top3 ? "bg-primary-500 text-background-50" : "bg-background-100 text-foreground-500"
                      }`}
                    >
                      {rank}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="truncate text-sm font-semibold text-foreground-950 md:text-base">{item.title}</h4>
                        {item.tag ? (
                          <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold whitespace-nowrap ${top3 ? "bg-accent-100 text-accent-800" : "bg-background-100 text-foreground-500"}`}>
                            {item.tag}
                          </span>
                        ) : null}
                      </div>
                      <p className="mt-1 hidden truncate text-xs text-foreground-500 sm:block">{item.summary}</p>
                      <div className="mt-2 flex items-center gap-3">
                        <div className="h-1.5 w-full max-w-[220px] overflow-hidden rounded-full bg-background-100">
                          <div className="h-full rounded-full bg-gradient-to-r from-primary-400 to-primary-500" style={{ width: `${item.heat}%` }} />
                        </div>
                        <span className="text-[11px] text-foreground-500">热度 {item.heat}</span>
                        <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-foreground-500 whitespace-nowrap">
                          <i className="ri-chat-3-line text-xs" /> {fmtCount(item.discuss)} 讨论
                        </span>
                      </div>
                    </div>
                  </article>
                );
              })
            )}
          </div>
          <p className="mt-6 text-center text-xs text-foreground-500">
            {shown.length > 0 ? `正在显示 ${catMap.get(activeCat) || "全部"} · 共 ${filtered.length} 条今日校园热点` : "榜单数据实时汇总中"}
          </p>
        </Reveal>
      </div>
    </section>
  );
}